import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const ExportModal = ({ isOpen, onClose, onExport }) => {
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [selectedFields, setSelectedFields] = useState({
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
    address: true,
    category: true,
    totalValue: true,
    totalQuotes: true,
    lastInteraction: true
  });
  const [exportFilters, setExportFilters] = useState({
    category: '',
    dateRange: 'all'
  });
  const [isLoading, setIsLoading] = useState(false);

  const formatOptions = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'pdf', label: 'PDF (.pdf)' }
  ];

  const categoryOptions = [
    { value: '', label: 'Toutes les catégories' },
    { value: 'individual', label: 'Particuliers' },
    { value: 'professional', label: 'Professionnels' },
    { value: 'subcontractor', label: 'Sous-traitants' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'last30', label: '30 derniers jours' },
    { value: 'last90', label: '90 derniers jours' },
    { value: 'thisYear', label: 'Cette année' }
  ];

  const fieldOptions = [
    { key: 'firstName', label: 'Prénom' },
    { key: 'lastName', label: 'Nom' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'address', label: 'Adresse' },
    { key: 'category', label: 'Catégorie' },
    { key: 'totalValue', label: 'Valeur totale' },
    { key: 'totalQuotes', label: 'Nombre de devis' },
    { key: 'lastInteraction', label: 'Dernière interaction' }
  ];

  const handleFieldChange = (field, checked) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedFields)?.every(Boolean);
    const newState = {};
    fieldOptions?.forEach(field => {
      newState[field.key] = !allSelected;
    });
    setSelectedFields(newState);
  };

  const handleExport = async () => {
    setIsLoading(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const exportData = {
        format: exportFormat,
        fields: selectedFields,
        filters: exportFilters,
        timestamp: new Date()?.toISOString()
      };

      onExport(exportData);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedFieldsCount = Object.values(selectedFields)?.filter(Boolean)?.length;
  const allFieldsSelected = Object.values(selectedFields)?.every(Boolean);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface border border-border rounded-lg shadow-soft-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Download" size={20} className="text-primary" />
            <h2 className="text-xl font-semibold text-text-primary">Exporter les données clients</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            disabled={isLoading}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Export Format */}
          <div>
            <Select
              label="Format d'export"
              options={formatOptions}
              value={exportFormat}
              onChange={setExportFormat}
              disabled={isLoading}
            />
          </div>

          {/* Export Filters */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary">Filtres d'export</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Catégorie de clients"
                options={categoryOptions}
                value={exportFilters?.category}
                onChange={(value) => setExportFilters(prev => ({ ...prev, category: value }))}
                disabled={isLoading}
              />
              <Select
                label="Période"
                options={dateRangeOptions}
                value={exportFilters?.dateRange}
                onChange={(value) => setExportFilters(prev => ({ ...prev, dateRange: value }))}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Field Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">Champs à exporter</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={isLoading}
              >
                {allFieldsSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
              </Button>
            </div>
            
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fieldOptions?.map((field) => (
                  <Checkbox
                    key={field?.key}
                    label={field?.label}
                    checked={selectedFields?.[field?.key]}
                    onChange={(e) => handleFieldChange(field?.key, e?.target?.checked)}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>
            
            <div className="text-sm text-text-secondary">
              {selectedFieldsCount} champ{selectedFieldsCount > 1 ? 's' : ''} sélectionné{selectedFieldsCount > 1 ? 's' : ''}
            </div>
          </div>

          {/* Export Preview */}
          <div className="bg-muted/30 border border-border rounded-lg p-4">
            <h4 className="font-medium text-text-primary mb-2">Aperçu de l'export</h4>
            <div className="text-sm text-text-secondary space-y-1">
              <div>Format: {formatOptions?.find(f => f?.value === exportFormat)?.label}</div>
              <div>Catégorie: {categoryOptions?.find(c => c?.value === exportFilters?.category)?.label}</div>
              <div>Période: {dateRangeOptions?.find(d => d?.value === exportFilters?.dateRange)?.label}</div>
              <div>Champs: {selectedFieldsCount} sélectionnés</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            loading={isLoading}
            iconName="Download"
            iconPosition="left"
            disabled={selectedFieldsCount === 0}
          >
            {isLoading ? 'Export en cours...' : 'Exporter'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;